/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { Brand } from './brand';

/**
 * Per-brand DESKTOP HEADER COMPOSITION — one keyed row per brand, never inline
 * branches (established idiom: `MINI_CART_BEHAVIOR` in ./mini-cart-behavior.ts,
 * `MINI_CART_SURFACE` in components/header/cart-badge.tsx, `NAV_SWITCH` in
 * navigation-menu-mega). A third brand is a COMPILE ERROR here rather than a
 * silent inheritance of NM's header.
 *
 * WHY A MAP AND NOT TOKENS. NM and BG do not render the same header with
 * different numbers — they render different ELEMENTS. No CSS value expresses
 * "search is a collapsed panel trigger, not a text field", "the utilities are
 * words, not glyphs", or "this brand has no gender toggle at all". Those are
 * structural facts and they belong in typed data.
 *
 * WHY ONE MAP AND NOT TWO. This row absorbed the former `HEADER_SWITCH`
 * (components/header/index.tsx, 2026-08-16) unchanged. The breakpoint gates and
 * the structural slots have to agree — BG's utility text labels appear at the
 * same width its icon glyphs disappear, and its search trigger appears at the
 * same width its hamburger does. Splitting them across two maps is how the
 * pre-HEADER_SWITCH bug happened (four gates hard-coded `md:` while the fifth
 * was forked), so they stay in one row.
 *
 * ── LIVE EVIDENCE, read directly from the captures (not from a summary) ──
 *
 * BG desktop header — `.reference/captures/bg-home-d-1280/App.tsx:71-116`
 * (copy 1 of the capture ends at :934; verified with `grep -a "import React"`).
 * The bar is `items-center flex justify-between … max-w-[1440px]` (:72) over
 * three `w-[33.3333%]` thirds:
 *   - LEFT third (:73-80) — a COLLAPSED SEARCH TRIGGER:
 *     `<div role="button" aria-haspopup="true" aria-label="Open search panel">`
 *     holding a 14x14 magnifier `<i>` (:76) and the literal word "Search" (:77).
 *     THERE IS NO `<input>` ANYWHERE IN THE HEADER. The panel's field only
 *     exists once opened — `.reference/captures/bg-searchbar-m-375/App.tsx:114`
 *     shows the opened panel: a full-width `<input type="text">` plus an
 *     `<input type="reset" aria-label="Close search">`.
 *   - CENTRE third (:81-91) — `role="banner"` wordmark link, unchanged from ours.
 *   - RIGHT third (:92-113) — three TEXT LINKS and no `<img>` glyph of any kind:
 *     "Sign in" (:96-98), "Wishlist" (:99), "Bag" (:107).
 * NOT PRESENT anywhere in :71-116: gender tabs, a My Orders link, a store
 * locator. `grep -ani "my orders"` over the whole capture returns nothing; the
 * only "men"/"women" hits are CATEGORY NAV entries in the separate `<nav>` at
 * :118-260 ("what's new / shop by / designers a-z / clothing / shoes / handbags
 * / accessories / jewelry / beauty / men / kids / home / sale"), i.e. BG ships
 * "men" as one of thirteen silos, not as a gender toggle.
 *
 * THIS LIST WAS WRONG IN TWO WAYS UNTIL 2026-08-22 while its COUNT stayed right,
 * which is why nothing caught it: it omitted "designers a-z" and it promoted a
 * "more" that is not a nav item at all. The silo discriminator in the capture is
 * the span carrying the uppercase transform at a 13px line box with a
 * transparent bottom border (the hover underline) — written as prose rather
 * than as the class string, because Tailwind's scanner treats any candidate in
 * a scanned source file as a usage and a comment must not be what keeps a rule
 * in the bundle — members run :124..:232 in the first emission and
 * :1058..:1166 in the second, thirteen each. The four "more" spans (:251, :286,
 * :313, :464) are absolutely-positioned, zero-height, white, text-shadowed
 * overlay labels belonging to the hero carousel, not siblings of "sale".
 * Corroborated on three raw dumps, which preserve live's own class names and
 * bound the list as thirteen `a.silo-link.clickable-silo` inside
 * `nav#silo-navigation > ul`: .reference/dumps/bg-plp-d-1280,
 * .reference/dumps/bg-search-d-1280 and .reference/bg-homepage.desktop.html all
 * return 13 silo links and 1 "designers a-z", and none has a "more" silo. Our
 * own BG nav seed already matches live 13/13 in order, so only this description
 * was ever wrong — the data was right.
 *
 * NOTE for a future pass — docs/archetypes/bg-deltas.md §2 "Header / Navigation"
 * reports BG gender-nav text `>Men< / >Women<` from the 2026-08-15 prod capture.
 * The 2026-08-17 capture above shows those strings are the category nav's, not a
 * gender toggle. Treat that line as superseded.
 *
 * NM desktop header — `.reference/captures/home-d-1280/App.tsx:93-161`: gender
 * tabs left (:97-108), logo centre (:109-115), and a right column stacking My
 * Orders (:117-119), the icon row (:120-142) and a real search form with a
 * visible submit (:144-161). Every NM row below is byte-for-byte what the
 * component rendered before this map existed.
 *
 * ── WHAT IS DELIBERATELY *NOT* HERE ──
 *
 * MOBILE — NO LONGER ABSENT FROM THIS MAP. This section used to say BG's mobile
 * composition was a separate divergence the map did not address. It does now,
 * through `mobileUtilities`, the two per-control mobile gates, and
 * `mobileRowTracks`.
 * `.reference/captures/bg-home-m-375/App.tsx:72-80` is the hamburger and the
 * search trigger in the left group, and `:98-105` is an EMPTY `<div></div>`
 * followed by the bag — no sign-in, no wishlist, in any form.
 *
 * NM mobile — `home-m-375/App.tsx:38-74` and the same band at 390 and 440: a
 * 42px hamburger, the centred wordmark, and a right group holding the sign-in
 * button and the bag. NO WISHLIST, at any of the three widths, on either the
 * home or the search surface. NM's mobile arm was ADDED WITHOUT BEING READ when
 * BG's landed — see {@link MobileUtilityPresentation} for the retired value and
 * the citation that was right while the claim built on it was wrong.
 *
 * What is still not here: where the removed controls GO. Live puts BG's Sign in
 * and Wishlist in the drawer's 56px top row (bg-drawer-375:335, :338), and NM's
 * favourites in ITS drawer as a "View Favorites" row linking
 * `/myfavorites/myFavoriteItems.jsp` (drawer-clothing-375:520-522,
 * drawer-open-old-375:495-497) — so BOTH brands relocate rather than drop.
 * `DRAWER_COMPOSITION` in components/navigation-menu-curated/mobile.tsx gives
 * that top row to `bag-only` brands only, which now leaves NM's wishlist
 * reachable from the header at no mobile width. This map removes controls from
 * the header bar; it does not relocate them, and the drawer is a separate
 * surface with its own owner.
 *
 * BAG BEHAVIOUR. Live BG's "Bag" is a plain `<a href="/cart/">` in this static
 * capture, but BG's own mini-cart dropdown is already an accepted brand fact
 * (`MINI_CART_BEHAVIOR.bg.presentation === 'dropdown'`, bg-deltas.md §2
 * "MiniCartDT"). Changing the bag from a mini-cart trigger to a navigation link
 * is a behaviour decision, not a structural read, so this map changes only how
 * the bag LOOKS, never what clicking it does.
 */

/**
 * Shape AND zone of the desktop search affordance, as one value so the pair can
 * never disagree.
 *
 * `right-inline-field` — a persistent `<input>` + visible submit in the right
 * column (NM, home-d-1280/App.tsx:144-161).
 * `left-panel-trigger` — a collapsed icon+word trigger in the LEFT third that
 * discloses the search field on activation; no input in the DOM until then
 * (BG, bg-home-d-1280/App.tsx:73-80).
 */
export type DesktopSearchAffordance = 'right-inline-field' | 'left-panel-trigger';

/**
 * How account / wishlist / bag present at desktop width.
 *
 * `icon-rail` — glyph buttons, sign-in additionally labelled (NM,
 * home-d-1280/App.tsx:120-142).
 * `text-links` — words only, no glyphs (BG, bg-home-d-1280/App.tsx:92-113).
 */
export type DesktopUtilityPresentation = 'icon-rail' | 'text-links';

/**
 * Which utility controls the MOBILE header carries at all.
 *
 * `account-and-bag` — sign-in glyph and bag, and NO wishlist in any form (NM).
 * `bag-only`        — the bag alone; neither sign-in nor wishlist exists in the
 * mobile header in ANY form (BG).
 *
 * NEITHER BRAND PUTS A WISHLIST IN ITS MOBILE HEADER. That is the whole content
 * of the correction below, and it is why the wishlist gate is per-brand
 * mandatory rather than paired with this value.
 *
 * ── `icon-rail` IS RETIRED, AND HOW IT GOT HERE MATTERS ──────────────────
 *
 * This union used to read `'icon-rail' | 'bag-only'`, where `icon-rail` meant
 * "sign-in AND wishlist render as glyphs beside the bag" and NM held it. NO
 * CAPTURE EVER SHOWED THAT. Its citation was `home-m-375/App.tsx:52-70`, and
 * that citation is real and correct — the problem is what it contains. Lines
 * 51-73 are the ENTIRE mobile right group: `:51` opens it, `:54-60` is the
 * `aria-label="Sign In and Register - Your Neiman's Account Panel"` button,
 * `:61-71` is the `aria-label="Shopping bag"` cart, `:72-73` closes it. There
 * is no third control. The evidence was read, pasted, and then summarised into
 * a value it does not support.
 *
 * Corroborated at every mobile width and on both surfaces we hold, each with a
 * positive control in the same command so an empty result cannot be a broken
 * search: `wishlist|favourite|favorite|heart` returns ZERO hits across
 * home-m-375, home-m-390, home-m-440, search-m-375, search-m-390 and
 * search-m-440, while `sign ?in` and `shopping bag` fire in all six.
 *
 * The corroborating witness used to be the nm-search-m-440 dump, cited for
 * live's own class names. That source is barred, and it is replaced by
 * something better for a PRESENCE question — the owner's own pixels.
 * `.reference/live-shots/nm-search-390.png`, converted to sRGB, 2.0 px/CSS px,
 * page origin x=602/y=238. The mobile brand row (page y 62..105) paints FOUR
 * ink runs and no more:
 *
 *     x  10.5 ..  31.0   hamburger glyph, centred in live's 42px box
 *     x 144.5 .. 244.0   wordmark, 100.0 wide
 *     x 318.0 .. 333.5   account
 *     x 361.5 .. 376.0   bag
 *
 * The right group is two controls. A heart would be a fifth run and there is
 * not one. This also settles the negative in a way a grep never can: it is a
 * statement about what live PAINTS, not about what a search matched.
 *
 * THE STYLESHEET IS NOT THE DOM, and this is the trap worth naming. Live's CSS
 * *does* define `.clean-header-utility-menu .favorite a .favo-icon
 * { height:15px; width:15px }`. The rules ship at every width; the ELEMENT is
 * rendered only from 768 up (`home-d-768` through `home-d-1920` all carry the
 * `alt="my favorite icon"` 15x15 `<img>`). A rule existing is evidence about
 * desktop, never about mobile. (That declaration was read out of the now-barred
 * dump's bundle and is left here as the SHAPE of the trap, not as a value —
 * nothing in this file depends on the 15x15 for mobile, and the mobile answer
 * is settled by the painted evidence above.)
 */
export type MobileUtilityPresentation = 'account-and-bag' | 'bag-only';

/**
 * BG's desktop min-width, in CSS px — the ONE place this boundary is written as
 * a number. Exported because the class gates are not its only consumer: a JS
 * `matchMedia` cannot read a Tailwind variant, and
 * `components/header/bg-mini-cart-dropdown.tsx` has to gate the same boundary
 * to decide which mini-cart SURFACE opens.
 *
 * MEASURED ON LIVE, 2026-08-20, from the owner's own boundary captures of BG's
 * PLP (`.reference/live-shots/owner-bg-plp-boundary-{1,2,3}.png`, shots 1 and 2
 * at ≤1024, shot 3 at ≥1025). The four discriminators, read off the pixels:
 *
 *   ≤1024 — hamburger, an icon-only magnifier, the centred wordmark, the bag
 *           glyph, and NO category nav row.
 *   ≥1025 — the WORD "Search" at the left, "Sign in" / "Wishlist" / "Bag" as
 *           TEXT links at the right, no hamburger, and the full thirteen-item
 *           category nav (WHAT'S NEW · SHOP BY · DESIGNERS A-Z · CLOTHING ·
 *           SHOES · HANDBAGS · ACCESSORIES · JEWELRY · BEAUTY · MEN · KIDS ·
 *           HOME · SALE, the last in red).
 *
 * THIS RETIRES THE (1024, 1280] BRACKET this file used to reason about, and it
 * is worth being precise about how the old answer went wrong. Both endpoints
 * were held — mobile confirmed at 1024 on three surfaces, desktop at 1280 on
 * four — and `xl` was picked because it was the only value on Tailwind's
 * default scale INSIDE the bracket. That is a statement about Tailwind, not
 * about BG. The bracket's real answer was its very bottom, so `xl` served BG's
 * mobile header across 1025-1279 where live serves the desktop one: an entire
 * band missing across 255px, on every page.
 *
 * THE LEGACY `$media-desktop-min: 1025px` IS STILL NOT THE EVIDENCE, and the
 * number now agreeing does not promote it. NM's own variables.scss declares the
 * identical 1025 while NM measurably switches at 768
 * (docs/bg-design-system.md:306-311) — a variable that mispredicts one brand by
 * 257px cannot be cited about the other. The captures above are the whole of
 * the support; the SCSS is a coincidence that happens to have been right.
 */
export const BG_DESKTOP_MIN_WIDTH_PX = 1025;

/**
 * The Tailwind variant BG's gate strings are spelled with — a NAMED CUSTOM
 * SCREEN, declared `--breakpoint-bgnav: 1025px` in theme/tailwind.css.
 *
 * WHY NOT A STOCK SCALE KEY. 1025 is not on Tailwind's default scale, and
 * `lg:` fires AT 1024 — one pixel early, which would put the desktop header on
 * the last width live still serves mobile. That is the exact error `xl` was
 * chosen to avoid, one endpoint over. No stock key lands here.
 *
 * WHY IT IS NAMED FOR THE SURFACE AND NOT FOR THE BRAND. BG runs TWO desktop
 * boundaries: its header and category nav switch at 1025, its FOOTER at 1024
 * (`lg:`). A `bgdesktop` name would read as one brand-wide fact and invite the
 * 1025 to be carried into footer work — which has already happened once as a
 * near-miss, caught in review. `bgnav` names what it governs, so a footer gate
 * written with it reads wrong on sight.
 *
 * ── WHAT REPLACED THE TYPE-LEVEL DERIVATION, AND WHY IT IS STRONGER ────────
 *
 * This type used to be a template literal built from `typeof
 * BG_DESKTOP_MIN_WIDTH_PX`, so the number and the class spelling could not
 * drift apart. A named screen cannot be derived that way — the variant is an
 * identifier, not a rendering of the number — so that guarantee is REPLACED,
 * not dropped: `src/__tests__/named-breakpoint-variants.test.ts` reads
 * theme/tailwind.css and asserts `--breakpoint-bgnav` is declared at exactly
 * `BG_DESKTOP_MIN_WIDTH_PX` px.
 *
 * That check is strictly STRONGER than the type it replaces. The old template
 * literal proved the class string RENDERED the number; it could not prove any
 * CSS implemented that boundary, because the arbitrary variant carried its
 * query inline and there was no declaration to check. The new assertion ties
 * the TS constant to the actual CSS declaration the utilities come from.
 *
 * SAME SPELLING AS `components/header/bg-search-panel.tsx`, which crossed this
 * boundary first (2026-08-20) for the same reason and on the same evidence.
 * Deliberately identical, so a grep for the boundary finds both.
 */
export type BgDesktopVariant = 'bgnav';

export interface HeaderComposition {
    // ── Paired mobile/desktop gates (formerly HEADER_SWITCH) ──────────────
    /**
     * The Tailwind VARIANT PREFIX at which this brand's desktop header takes
     * over — literally the text before the `:` in every gate string below, not
     * a decorative label. NM is `md` (768); BG is `bgnav`, a named custom
     * screen at a measured 1025 — see {@link BG_DESKTOP_MIN_WIDTH_PX} for the
     * captures and {@link BgDesktopVariant} for why it is not a stock key and
     * why it is named for the SURFACE rather than the brand.
     *
     * ── WHY THIS FIELD IS THE VARIANT AND NOT A FRIENDLIER LABEL ─────────
     *
     * `header-composition.test.ts` derives every gate from it —
     * `mobileOnly === `${breakpoint}:hidden`` and three siblings — so ONE value
     * pins all five gates plus the zone strings, and a half-move is a test
     * failure rather than a rendering surprise 255px wide. That guard is the
     * single most valuable thing in this file's test, and it only works while
     * the field is substitutable into a class string.
     *
     * It surfaces as a DOM attribute reading `data-header-breakpoint="bgnav"`,
     * with a stylesheet selector to match (theme/overrides/navigation.css).
     * Both halves must move together. A tidier `'1025px'` was considered and
     * declined even now: it would break the derivation above and force the
     * guard to be rewritten as a hand-rolled prefix comparison, trading the
     * strongest check in the file for a prettier attribute.
     *
     * THAT COST USED TO BE THE REAL ONE AND NO LONGER IS. While the boundary
     * was an arbitrary media variant, this attribute carried a bracketed query
     * as its value and the matching selector had to quote it — legible to
     * nobody, and the paragraph here defended it as a deliberate trade. The
     * named screen removes the trade entirely: the derivation still holds, and
     * the attribute now reads as a word.
     *
     * NO NEW STOCK KEYS. `md` / `lg` / `xl` remain the only built-in Tailwind
     * screens this field takes; the fourth member is a NAMED CUSTOM screen
     * declared in theme/tailwind.css, admitted because live's boundary is
     * measurably OFF the default scale and no built-in key lands on it. A
     * brand whose switch IS on the scale still uses the key — and a new custom
     * screen is a deliberate act with a `@theme` declaration behind it, not an
     * inline escape hatch anyone can open at a call site.
     *
     * ── WHAT THE MOVE OFF `xl` COST AND SETTLED ──────────────────────────
     *
     * `xl` was not a careless pick: 1024 was confirmed MOBILE on three
     * independent surfaces (bg-home-d-1024, bg-search-d-1024, bg-pdp-d-1024 —
     * one `h-[69px]` band, an `alt="Mobile navigation icon"` hamburger, an
     * icon-only `aria-label="Open search panel"` trigger, and no `>Search<`,
     * `>Sign in<`, `>Wishlist<` or `designers a-z`), corroborated at 768
     * (bg-pdp-d-768, bg-search-d-768); and 1280 was confirmed DESKTOP on four
     * (bg-home-d-1280, bg-pdp-d-1280, bg-search-d-1280, bg-minicart-d-1280,
     * plus bg-pdp-d-1440). Both endpoints were right. The bracket between them
     * was never measured, and its answer turned out to be its bottom.
     * (bg-cart-d-1024 was and remains no evidence either way: BG's cart is a
     * separate unlisted service — docs/archetypes/bg-deltas.md:35-43 — and
     * carries none of the storefront header's controls at any width.)
     *
     * THE LESSON WORTH KEEPING is not "1025" but that a value chosen because it
     * is the only one available on some OTHER system's scale is an artifact of
     * that scale, not a reading. The bracket had two endpoints and 255 unknown
     * widths; picking from the scale silently asserted one of them.
     *
     * OUR OWN CSS TRACKS THIS FIELD AND HAS TO MOVE WITH IT.
     * tokens/brand-bg.css brackets --header-main-height to BG's measured 69px
     * mobile band BELOW this boundary and hands over the desktop band rects
     * above it; the two must be the same number or a desktop band paints around
     * a mobile header, or the reverse. Both blocks moved with this field, and
     * bg-tokens.test.ts pins them at the new width.
     */
    breakpoint: 'md' | 'lg' | 'xl' | BgDesktopVariant;
    /** Hamburger wrapper and the full-width mobile search row. */
    mobileOnly: string;
    /** Gender tabs (flex row) — only rendered when `genderTabs` is true. */
    desktopFlex: string;
    /** Desktop search slot and the curated nav band. */
    desktopBlock: string;
    /**
     * The desktop half of an INLINE affordance — the sign-in label NM already
     * had, and the utility words BG shows in place of glyphs. Paired with
     * `mobileOnly` on the glyph it replaces, which is why it lives in the same
     * row rather than being spelled out at each call site.
     */
    desktopInline: string;

    // ── Structural slots ──────────────────────────────────────────────────
    /** Gender toggle in the main row. BG has none (bg-home-d-1280:71-116). */
    genderTabs: boolean;
    /** "My Orders" quick link above the utilities. BG has none (:92-107). */
    myOrders: boolean;
    /** See {@link DesktopSearchAffordance}. */
    desktopSearch: DesktopSearchAffordance;
    /** See {@link DesktopUtilityPresentation}. */
    desktopUtilities: DesktopUtilityPresentation;
    /** See {@link MobileUtilityPresentation}. */
    mobileUtilities: MobileUtilityPresentation;
    /**
     * COMPLETE class string that removes the SIGN-IN control from the mobile
     * header and restores it at this brand's breakpoint.
     *
     * Empty for brands that render sign-in at every width, so their class
     * strings stay BYTE-IDENTICAL (they are pinned in the storybook snapshots).
     * It has to carry the display utility itself rather than being appended to
     * one — `hidden` and `inline-flex` are the same tailwind-merge conflict
     * group, so a gate that only said `hidden` would be resolved away against
     * the control's own display class depending on order.
     */
    signInMobileGate: string;
    /**
     * The same, for the WISHLIST control — and a separate field because the two
     * controls measurably disagree. NM keeps its sign-in glyph at 375/390/440
     * and its wishlist is absent; BG has neither. One shared gate could only
     * ever say "both" or "neither", which is precisely how NM shipped a heart
     * live NM does not render: the single gate was empty for NM because NM's
     * sign-in belongs there, and the wishlist rode along on that answer.
     *
     * MANDATORY FOR EVERY BRAND — no measured brand puts a wishlist in its
     * mobile header, so an empty value here is a bug, and the composition test
     * asserts non-empty unconditionally rather than pairing it with
     * `mobileUtilities`. That is the assertion that would have caught this.
     */
    wishlistMobileGate: string;
    /**
     * COMPLETE class string giving the MOBILE main row its track model, or the
     * empty string to keep the row's own `flex`.
     *
     * WHY A BRAND NEEDS TO SAY THIS. The mobile row is [left group | wordmark |
     * right group] and the two side groups are different widths, because the
     * left is one hamburger and the right is a control cluster. Under `flex`
     * with a `flex-1` centre track the wordmark centres on the SPACE BETWEEN
     * the groups, not on the viewport, and the error is exactly half their
     * width difference. MEASURED on ours at `data-brand=nm`, guards inside the
     * evaluate: left 36px, right 140px, wordmark centre 52.01px left of the
     * viewport centre at 375, 390 AND 440 — (36-140)/2 = -52 at every width,
     * which is the mechanism confessing itself.
     *
     * LIVE NM HAS THE SAME IMBALANCE AND CORRECTS IT.
     *
     * RE-SOURCED 2026-08-19. This paragraph was measured off the barred
     * nm-search-m-440 dump. The CONCLUSION survives on two non-dump sources;
     * one of its declarations does not, and is demoted rather than deleted.
     *
     * SURVIVES — the boxes, from `.reference/captures/search-m-390/App.tsx:68`:
     * the bar is `flex justify-between relative w-full h-[45px]`, the hamburger
     * is `w-[42px]`, and the right group totals 79 (account button
     * `h-[26px] px-[6px]` over an 18px icon with `pr-[7px]` = 37, plus the bag's
     * `w-[42px]`). That is the same 42-vs-79 imbalance, an 18.5px pull left,
     * read without the dump.
     *
     * SURVIVES — the outcome, from the owner's `.reference/live-shots/
     * nm-search-390.png` (sRGB, 2.0 px/CSS px): the wordmark's ink runs
     * x=144.5..244.0, so it is 100.0 wide and centred at 194.25 against a
     * viewport centre of 195. **Net offset -0.75px**, measured — the dump's
     * -0.5px was right to within a quarter pixel. Live's wordmark IS centred on
     * the VIEWPORT, and given the 42/79 imbalance it takes an explicit ~18px
     * correction to get there. That is the whole load-bearing claim.
     *
     * DEMOTED — the declaration. The dump attributed the correction to
     * `.brand-logo-clean-header-mobile.shift-right { transform: translateX(18%) }`
     * on a 100px box. The 100px box is confirmed by the pixels above, and a
     * shift of ~18px is REQUIRED by them, but the capture's own computed value
     * for that span is `translate-x-[1.22369e-09%]` — i.e. zero. One of the two
     * is a frozen-frame artifact and we cannot tell which. Treat the 18%
     * literal as unmeasured; the mechanism (an explicit correction exists) is
     * what this file relies on and that is measured.
     *
     * WHY GRID AND NOT LIVE'S 18px. That 18px is not a design value, it is
     * `(79-42)/2` rounded — the arithmetic of one particular control cluster.
     * Ours has different control widths, so copying the number copies nothing.
     * Equal `1fr` side tracks around an `auto` centre express the INTENT
     * ("centre the wordmark on the viewport") and survive a control changing
     * width, which a literal offset does not. It is also not a new idea here:
     * the desktop arm of this same row has been `md:grid-cols-[1fr_auto_1fr]`
     * all along, for this reason, in this file's own words — "equal side tracks
     * keep the logo viewport-centered". Mobile simply never got it.
     *
     * WHY `auto` AND NOT A THIRD `1fr`. Three equal tracks would cap the
     * wordmark at a third of the row. Ours renders 124.86px wide and a third of
     * the row is 114.33px at 375 and 119.3px at 390, so equal thirds would
     * SHRINK the mark at both — a regression bought with a centring fix. An
     * `auto` centre track takes the mark's natural width and splits what is
     * left equally, which centres it without touching its size.
     *
     * RESIDUAL, and it is a real one: `1fr` is `minmax(auto, 1fr)`, so a side
     * track never shrinks below its own content. At 375 the right cluster is
     * 96px against a 101.07px track — 5px of headroom. Adding a fourth control
     * to the mobile icon row (the `showChat` button is the live candidate)
     * re-opens the offset at 375. `minmax(0,1fr)` would hold the centre and
     * clip instead, which is worse; the honest fix at that point is fewer
     * mobile controls, which is what both brands' live headers already do.
     *
     * Empty on brands that keep `flex`, so their row string is byte-identical.
     */
    mobileRowTracks: string;
    /**
     * COMPLETE class string overriding the MOBILE wordmark artwork's sizing, or
     * the empty string to leave the shared string alone.
     *
     * THIS IS THE HALF OF `mobileRowTracks` THAT MAKES IT WORK, and it is not
     * cosmetic. The shared mobile art string is `max-h-<token> h-auto` plus a
     * width constraint: a replaced element with auto height, an auto width and
     * only a MAXIMUM height. That sizes fine inside a flex item, whose width the
     * flex algorithm has already resolved — but it contributes NOTHING to an
     * intrinsic grid track, because there is no definite height for the
     * aspect-ratio transfer to turn into a width. MEASURED: with the row on
     * `grid-cols-[1fr_auto_1fr]` and the art left as-is, Chrome resolves the
     * tracks to `163.5px 0px 163.5px` and the wordmark renders 0x0. The
     * `logoArtOffsetFromCentre` probe read a clean `0` for it, which is the
     * textbook vacuous pass — a zero-size box satisfies any position
     * assertion, so the number was right and meant nothing. Only the picture
     * and the width caught it.
     *
     * Swapping the cap for the height itself — the SAME token, not a new value
     * — gives the transfer something to work with: a definite height, the
     * asset's own ratio, and therefore a real width contribution. The wordmark
     * then lands on the viewport centre instead of on the centre of the space
     * between two different-width control groups.
     *
     * THE ARITHMETIC IN THIS PARAGRAPH WAS RE-MEASURED 2026-08-20 AND THE
     * FIGURES MOVED, because `--header-logo-art-height-mobile` is 39px for NM
     * (tokens/brand-nm/header.css) where this paragraph was written against 48.
     * The mechanism is unchanged and the outcome is now BETTER than the 0.01px
     * it recorded. Guarded in-browser reads, `data-brand=nm`, values are the
     * `<img>`'s own rect and `document.documentElement.clientWidth` is what was
     * GOT, not what was asked for:
     *
     *     375   101.45 x 39   centre 187.495  vs viewport centre 187.5   -0.005
     *     390   101.45 x 39   centre 195.00   vs 195                      0.000
     *     440   101.45 x 39   centre 220.00   vs 220                      0.000
     *
     * The mark is the same size at all three widths, which is the tell that the
     * definite height — not the track — is the binding constraint.
     *
     * `max-h-none` is deliberate rather than left in place: with the height set
     * from the token, a cap reading the same token is noise, and a reader
     * finding both would reasonably wonder which one is load-bearing.
     *
     * NOT A SHARED FIX, and that is the whole reason it is a field. BG's mobile
     * art is max-height-capped AND width-capped, and the WIDTH is what binds:
     * `brandRowLogoSize` (components/header/index.tsx) carries live BG's
     * measured `max-w-[108px]`, and BG's mark renders 108.00 x 40.14 at 375,
     * 390 AND 440 alike — a constant, so the cap is reached at every mobile
     * width rather than only at the narrow one. (This paragraph previously read
     * `max-w-full` binding at 375 only, for 223px against 234.11px at 390/440.
     * That was true of the wide lockup before the stacked asset and the 108px
     * cap landed; re-measured 2026-08-20, it is not true now.) Giving BG a
     * definite height would fight that cap and move a header landed from BG's
     * own captures.
     */
    mobileLogoArt: string;

    // ── Zone class strings ────────────────────────────────────────────────
    // COMPLETE static class strings — never computed `${bp}:hidden` — so
    // Tailwind's scanner can see them. NM's three strings are verbatim what the
    // component carried before this map existed; `header-composition.test.ts`
    // pins them so an edit to BG's row cannot drift NM's.
    /** Main-row LEFT track. NM: bottom-anchored gender tabs. BG: centred search trigger. */
    leftZone: string;
    /** Main-row RIGHT track. NM: top-anchored vertical stack. BG: one centred row of links. */
    rightZone: string;
    /** The utilities row itself, inside `rightZone`. */
    utilityRow: string;
    /**
     * GATE for the right zone's paint offset — `right-(--header-right-zone-inset)`.
     *
     * The offset itself is a token; what this field decides is the WIDTH BAND it
     * applies over, which is why it is a gate and not a value. It is built from
     * `breakpoint` like every other gate here, so the "one brand, one boundary"
     * assertion in the test covers it.
     *
     * WHY IT IS GATED AT ALL, measured rather than assumed. The offset was
     * harvested from live BG's right column, which computes `right: 18px`
     * (bg-home-m-375/App.tsx:96, bg-search-d-768/App.tsx:93). That column is
     * `position: static` in the same capture — it declares no `relative`,
     * `absolute` or `fixed` — so on live the property RESOLVES AND PAINTS
     * NOTHING. We applied it to a `relative` element, where it does paint, and
     * it walks BG's mobile bag 18px in from where live puts it. Colour-managed
     * read of the owner's live capture (.reference/live-shots/bg-search-390.png,
     * address bar `bergdorfgoodman.com`, device bar `iPhone 12 Pro 390 x 844`,
     * DPR 2 from a 780px page strip): live's bag INK right edge sits 16.5px from
     * the viewport edge at 390. Ours sat at 48.
     *
     * Retiring the offset outright is NOT what this does, and the restraint is
     * deliberate: the desktop band above each brand's breakpoint is a reviewed
     * surface, and the same reading has not been taken there. So the gate keeps
     * today's paint at breakpoint-and-up on both brands and drops it below,
     * which is the only band with a live number behind it.
     *
     * NM IS UNMOVED BY CONSTRUCTION: `--header-right-zone-inset` is `0px` for NM
     * (theme/tokens/header.css:130 is the shared default and NM declares no
     * override), so gating a zero offset paints identically at every width.
     */
    rightZoneOffset: string;

    // ── Glyph box strings ─────────────────────────────────────────────────
    // COMPLETE static class strings carrying NO breakpoint prefix — these are
    // box models, not gates, and the two brands' glyphs differ in ASPECT rather
    // than in the width at which they appear.
    /**
     * The bag glyph's own box. NM's string is byte-identical to what
     * `cart-badge-icon.tsx` hardcoded before this field existed, so its
     * storybook snapshots are unchanged.
     *
     * MUST CONTAIN A `size-` TOKEN whatever the values are: the shadcn Button
     * wrapping this icon forces `[&_svg:not([class*='size-'])]:size-4`, so a
     * `w-`/`h-` pair alone is silently squashed to 16x16.
     */
    cartGlyphBox: string;
    /**
     * Horizontal padding on the bag CONTROL below `md`, where the shared class
     * string otherwise leaves the shadcn Button's stock `px-4`.
     *
     * That 16px is framework chrome, not a measured rect — cart-badge.tsx has
     * said so in prose ("mobile keeps the stock tap target") since the box
     * tokens landed. NM keeps it; BG zeroes it, because live BG's own control
     * has no padding at all and its bag art carries the inset internally. See
     * BG's arm for the arithmetic and for why a compensation must retire in the
     * same change as the thing it compensated for.
     */
    cartTriggerMobilePadX: string;
    /**
     * Horizontal padding on the bag CONTROL at md+, as a complete class string.
     *
     * A FIELD RATHER THAN A TOKEN VALUE because the two brands need different
     * SHAPES here, not different numbers: NM's bag pads symmetrically, BG's
     * desktop bag must pad on the RIGHT ONLY, and one `px-` token cannot say
     * that. Live BG's Bag is a bare `<a>` with `pt-[10px] pr-0 pb-[10px] pl-0`
     * inside a wrapper carrying `pr-5` (bg-home-d-1280/App.tsx:106-107, one
     * occurrence each after halving, control `div` 1042) — so every pixel of
     * live's horizontal padding is on the right, and the left is zero.
     *
     * THE RIGHT PAD IS FRAMEWORK CHROME CONSCIOUSLY KEPT, and the distinction
     * matters. It is the stock 1rem, not live's 20px, because our utility
     * cluster's right edge already sits ~6px inside live's — `right-[18px]`
     * against live's inert copy of the same property — and 16px against that
     * edge lands our painted Bag at a right inset of 102.0 versus live's 100.5.
     * Changing it to live's 20 would move a verified edge to 106. So the shape
     * is live's and the magnitude is ours, deliberately.
     *
     * NM'S VALUE IS BYTE-IDENTICAL to the `md:px-(--header-cart-box-pad-x)` that
     * cart-badge.tsx carried inline, in the same position in the same string, so
     * NM's rendered class attribute and its pinned storybook snapshots are
     * unchanged BY CONSTRUCTION rather than by luck.
     */
    cartBoxPadX: string;
    /**
     * The mobile hamburger's own box, in navigation-menu-curated/mobile.tsx.
     *
     * BRAND-SCOPED ON MEASURED EVIDENCE, not on caution. The two brands do not
     * share this control: they use DIFFERENT ASSETS and declare DIFFERENT BOXES.
     *
     *   live BG  <img className="overflow-clip w-[30px]">   in a bare
     *            `align-middle mt-[5px] mr-[5px]` wrapper   (bg-home-m-375)
     *            asset h2d-ext-asset/129ef038...svg
     *   live NM  <img> with NO size class, inside a
     *            `text-center align-middle w-[42px] h-7 mt-[14px]` wrapper
     *            (home-m-390)  asset h2d-ext-asset/51dd7333...svg
     *
     * and their painted INK differs by more than a scale factor — measured off
     * the owner's own live captures at clientWidth 390, DPR 2, page strip
     * located from each shot's black ticker:
     *
     *   live BG  26.5 x 15.0  (aspect 1.767)   .reference/live-shots/bg-search-390.png
     *   live NM  20.0 x 14.5  (aspect 1.379)   .reference/live-shots/nm-search-390.png
     *
     * A single shared `size-[30px]` would have taken NM's ink to 23.0 x 20.0
     * against a live target of 20.0 x 14.5 — overshooting a brand that was never
     * measured until now. NM therefore keeps `size-5` BYTE-IDENTICALLY.
     *
     * BOTH BRANDS NOW HOLD LIVE'S OWN DRAWING, AND NEITHER BOX MOVED TO GET
     * THERE. That is the reusable lesson: these two values were verified as
     * boxes, reported as parity, and were carrying the wrong marks the whole
     * time. `mobileMenuGlyph` in navigation-menu-curated/mobile.tsx chooses the
     * drawing; this field only ever sized it. A box comparison cannot see a
     * silhouette, so the check that closes this class is INK, per brand, against
     * live's own asset — not a box diff.
     */
    mobileMenuGlyphBox: string;

    /**
     * The hamburger BUTTON's own box — the container the field above sizes a
     * glyph inside of. These are two different boxes and the distinction is the
     * point: `mobileMenuGlyphBox` is INK, this is the tap target around it, and
     * only this one is framework chrome today. The control is a shadcn
     * `Button size="icon"`, whose `size-9` is a UI-kit default that no one
     * measured for either brand — a stock component height standing in for a
     * value, which is exactly the class of thing that must be stripped or
     * consciously kept once a measured rect exists.
     *
     * NM — MEASURED, 42 x 28. Live NM's hamburger is a div with the asset
     * inside it, and the box is declared on the div:
     *
     *   home-m-390/App.tsx:84
     *   <div className="text-center align-middle w-[42px] h-7 mt-[14px]">
     *     <a role="button" …><img alt="Navigation Menu" …>
     *
     * `w-[42px] h-7 mt-[14px]` is byte-identical at home-m-375:39 and
     * home-m-440:39, so it is the whole mobile tier rather than one width.
     * Corroborated at a second tier by .reference/homepage.mobile.styles.json
     * -> hamburger, resolved getComputedStyle off live at 390: width 42px,
     * height 28px, margin 14px 0px 0px. Two independent sources, same three
     * numbers.
     *
     * THE PICTURE CONFIRMS THE NUMBER. A 42px box centring the untouched 20px
     * glyph puts its ink at x = (42-20)/2 = 11 — and 11 is the live hamburger x
     * that components/header/index.tsx has recorded from COMPARISON.md since
     * before this row existed. Ours rendered the glyph at x=8, which is what a
     * 36px box gives. The box width is the thing that was wrong; the glyph is
     * not, and does not move (viewBox 0 0 97 71.43, ink 20.00 x 14.67 against
     * live's 20.0 x 14.5).
     *
     * The margin half of that same declaration lands as
     * --header-hamburger-margin-top in theme/tokens/brand-nm/header.css, beside
     * BG's, because that token already exists for this element.
     *
     * BG — UNMEASURED, and the empty string is how that is said. BG's captures
     * declare a box on the IMG (`w-[30px]`, already carried by
     * mobileMenuGlyphBox) and nothing on the control around it, so there is no
     * live rect to read. Empty keeps BG on the stock `size-9` it renders today,
     * byte-identical — the same construction `cartTriggerMobilePadX` uses for
     * the mirror case, and the reason BG's header cannot move on this row.
     */
    mobileMenuBox: string;
}

export const HEADER_COMPOSITION: Record<Brand, HeaderComposition> = {
    // NM ARM PROVENANCE — re-derived 2026-08-19. THIS IS THE ARM THE BUG CLASS IS
    // NAMED FOR, so the region is quoted rather than pointed at.
    //
    // .reference/captures/home-m-375/App.tsx:51-73 is the ENTIRE mobile right group
    // and it holds exactly TWO controls: a `Sign In and Register - Your Neiman's
    // Account Panel` button wrapping a 21x25 glyph (:54-60), then a `view cart` /
    // `Shopping bag` control (:61-70). There is no third control. That is what
    // `mobileUtilities: 'account-and-bag'` records, and it is the same region the
    // retired `'icon-rail'` value cited while claiming a wishlist glyph — the
    // citation was accurate and the reading was not.
    //
    // The wishlist gate is confirmed by width, not by that region: live labels the
    // control FAVORITES, not "Wishlist", and it is absent at home-m-375 / -390 /
    // -440 and present at home-d-768 / -1024 / -1280. Searching for "wishlist"
    // returns 0 at every width and would read as "live has none anywhere"; the
    // `div` control is non-zero in all six files, so the zero is about the word.
    nm: {
        breakpoint: 'md',
        mobileOnly: 'md:hidden',
        desktopFlex: 'hidden md:flex',
        desktopBlock: 'hidden md:block',
        desktopInline: 'hidden md:inline',
        genderTabs: true,
        myOrders: true,
        desktopSearch: 'right-inline-field',
        desktopUtilities: 'icon-rail',
        mobileUtilities: 'account-and-bag',
        // NM renders sign-in at every width — home-m-375:54-60 and the dump's
        // `#your-neimans-cta` — so its gate stays the empty string and the
        // rendered sign-in class is byte-for-byte what it was.
        signInMobileGate: '',
        // The wishlist is the correction. `md:` is not a guess: the glyph is
        // absent at 375/390/440 and present at home-d-768, so live's own switch
        // and NM's `breakpoint` are the same 768. `inline-flex` is the display
        // the shadcn Button resolves to, so restoring it at `md` puts the
        // control back exactly as the desktop header had it.
        wishlistMobileGate: 'hidden md:inline-flex',
        // See `mobileRowTracks`: equal side tracks around an auto-width
        // wordmark, which is what the `md:` arm of this row already does.
        mobileRowTracks: 'grid grid-cols-[1fr_auto_1fr]',
        // The same token the cap already read — see `mobileLogoArt`.
        mobileLogoArt: 'h-(--header-logo-art-height-mobile) max-h-none',
        leftZone:
            'shrink-0 flex items-center md:flex-1 md:min-w-0 md:items-end md:pb-(--gender-tab-bottom-inset) md:translate-y-(--gender-tab-offset-y)',
        rightZone:
            'shrink-0 flex items-center justify-end md:flex-1 md:min-w-0 md:flex-col md:items-end md:justify-start md:pt-(--header-utility-top-inset)',
        utilityRow: 'flex items-center md:gap-x-(--header-icon-row-gap) md:mt-(--header-utility-gap-below-orders)',
        // NM's inset is the shared 0px, so this gate paints identically to the
        // ungated class it replaces. Verified in-browser, brand `nm`, before and
        // after: bag svg box 20x17 at x=354 (clientWidth 390), x=718
        // (clientWidth 768) and x=1230 (clientWidth 1280) — all three unchanged.
        rightZoneOffset: 'md:right-(--header-right-zone-inset)',
        // Byte-identical to the string cart-badge-icon.tsx carried inline, and
        // pinned that way in stories/__snapshots__/index-snapshot.tsx.snap:213.
        // Live NM 1440: svg 20x17 (les-blast-1-header-1280.md "Cart container").
        cartGlyphBox: 'size-5 h-(--header-icon-glyph-cart-height)',
        // Empty keeps NM on the stock tap target it renders today.
        cartTriggerMobilePadX: '',
        // Byte-identical to what cart-badge.tsx spelled inline, in the same
        // position in the same string — NM's snapshots cannot move.
        cartBoxPadX: 'md:px-(--header-cart-box-pad-x)',
        // UNCHANGED, and the value is the point — see `mobileMenuGlyphBox`. This
        // 20px box was never the defect: with live NM's own art in it the glyph
        // inks 20.00 x 14.67 against live's scanned 20.0 x 14.5, so the box was
        // right all along and the DRAWING was wrong. The lucide mark it used to
        // hold inked 15.33 x 13.33 in this same box (pixel scan at clientWidth
        // 390) because it is stroked on a square grid and cannot reach 1.358 at
        // any size. See components/icons/nm-hamburger-icon.tsx.
        mobileMenuGlyphBox: 'size-5',
        // MEASURED, and it is the BOX rather than the ink — home-m-390/App.tsx:84
        // `<div className="text-center align-middle w-[42px] h-7 mt-[14px]">`,
        // byte-identical at home-m-375:39 and home-m-440:39, and corroborated by
        // .reference/homepage.mobile.styles.json -> hamburger (42 x 28,
        // margin 14px 0px 0px, getComputedStyle off live at 390). `h-7` IS 28px.
        // This replaces the shadcn `size-9` the control inherited; see the field
        // docs for why 42 is also what puts the untouched glyph at live's x=11.
        mobileMenuBox: 'w-[42px] h-7',
    },
    // BG ARM PROVENANCE — structure re-derived 2026-08-19 from BG's own home
    // captures, with `div` as the positive control in every count and raw counts
    // halved (each page is emitted twice); the BOUNDARY re-measured 2026-08-20
    // from the owner's live boundary shots.
    //
    // THE STRUCTURE AND THE BOUNDARY ARE TWO SEPARATE READINGS and only one of
    // them moved. bg-home-d-1024 is still the discriminating capture for the
    // structure — Sign in and Wishlist ABSENT there, PRESENT at bg-home-d-1280,
    // the bag the only utility in bg-home-m-375 — which is what
    // `mobileUtilities: 'bag-only'` and the two gates below record, and it is a
    // reading rather than a default.
    //
    // What that capture could never settle is WHERE between 1024 and 1280 the
    // swap happens, and the previous revision resolved that by taking the only
    // Tailwind screen inside the bracket. The owner's boundary shots put it at
    // 1025 — the bracket's floor — so BG shipped its mobile header across
    // 1025-1279 where live serves the full desktop one. See
    // BG_DESKTOP_MIN_WIDTH_PX above for the pixels.
    bg: {
        breakpoint: 'bgnav',
        mobileOnly: 'bgnav:hidden',
        desktopFlex: 'hidden bgnav:flex',
        desktopBlock: 'hidden bgnav:block',
        desktopInline: 'hidden bgnav:inline',
        genderTabs: false,
        myOrders: false,
        desktopSearch: 'left-panel-trigger',
        desktopUtilities: 'text-links',
        mobileUtilities: 'bag-only',
        // `inline-flex` is the display both gated controls resolve to at
        // desktop (the shadcn Button's own base), so restoring it at the
        // boundary puts them back exactly as they were. The two halves carry
        // the same string because they gate the same measured fact — neither
        // control exists in BG's mobile header in any form — and they are
        // separate fields because NM's two DISAGREE, which is the whole reason
        // the single `utilityMobileGate` was split.
        signInMobileGate: 'hidden bgnav:inline-flex',
        wishlistMobileGate: 'hidden bgnav:inline-flex',
        // BG's mobile row keeps `flex`. Its wordmark sits 4px right of the
        // viewport centre — measured, landed from BG's own captures, and NOT
        // this change's business. NM's -52px is a different order of magnitude
        // with a different cause; touching BG's row to chase 4px would be
        // exactly the "assume the other brand agrees" move that produced the
        // NM defect in the first place.
        //
        // RE-CONFIRMED 2026-08-20 at three widths, and the 4px is EXACT rather
        // than approximate — `<img>` centre 191.50 / 199.00 / 224.00 against
        // viewport centres 187.5 / 195 / 220 at clientWidth 375 / 390 / 440.
        // A constant offset at three widths is a fixed asymmetry in the flex
        // row, not a rounding artifact. For scale: live BG's own mark is +1.5
        // off centre at 390 (ink x 144..249, centre 196.5, from the owner's
        // .reference/live-shots/bg-search-390.png at 2.0 px/CSS px), so the
        // divergence this leaves on the table is 2.5px, not 4.
        mobileRowTracks: '',
        // BG keeps `flex`, so its art needs no definite width to contribute to
        // an intrinsic track. Its mobile mark is left exactly as landed: a
        // constant 108.00 x 40.14 at 375, 390 and 440, from the `max-w-[108px]`
        // in `brandRowLogoSize` (components/header/index.tsx) against the
        // stacked asset. Live BG's paints 105 x 39 at 390 by the same
        // colour-managed read as above — +3 / +2, which is glyph noise.
        mobileLogoArt: '',
        // Live BG's bar is `items-center flex justify-between` with three equal
        // thirds (bg-home-d-1280/App.tsx:72-73, :92) — no bottom-anchoring and
        // no vertical stack, so BG takes neither the gender-tab bottom inset nor
        // the utility-column top inset that NM's zones carry.
        // EQUAL THIRDS AT EVERY WIDTH, which is live's own mechanism and was
        // previously only reproduced above 1025. Live BG's side columns are
        // `grow basis-0` at 375, 440 and 768 and `w-[33.3333%]` at 1024, 1280
        // and 1440 (the right column's string is quoted in full in
        // components/header/index.tsx beside the offset gate) — equal tracks
        // either way, which is what viewport-centres the wordmark no matter how
        // wide the glyph groups are.
        //
        // WHY IT HAD TO CHANGE WITH THE GLYPH WORK, measured not assumed: BG's
        // mobile zones were `shrink-0`, so the wordmark centred in the SPACE
        // BETWEEN them rather than on the viewport, and the old sizes happened
        // to cancel out (left group 76px, right group 52px — wordmark centre
        // 199.0 against a viewport centre of 195.0). Correcting the magnifier
        // (+11px left) and the bag control (-21px right) unbalanced them and
        // walked the wordmark to a centre of 215.0. With `flex-1` the same
        // browser read gives img 108 x 40.14 at x=142.5, centre 196.5 — against
        // live's ink centre of 196.25, read off
        // .reference/live-shots/bg-search-390.png at clientWidth 390.
        //
        // `flex-1` is `flex: 1 1 0%` — `grow` and `basis-0` in one utility — and
        // it makes the two `bgnav:` gates that used to
        // carry it redundant, so they are gone rather than left to imply a step
        // that no longer exists.
        // TOP-ANCHORED AT DESKTOP, and this RECONCILES the paragraph above rather
        // than overwriting it. That note is right that live's bar row is
        // `items-center flex justify-between` over three equal thirds — I
        // re-read bg-home-d-1280/App.tsx:72, :73, :81 and :92 and every third
        // also carries a symmetric `pt-3`/`pb-3`. What does NOT follow from that
        // markup is the conclusion that our zones may centre: live's thirds do
        // not centre their CONTENT the way our stretched grid children do, and
        // the painted pixels are the arbiter.
        //
        // Ours sat 10.0px (right zone) and 11.5px (left zone) below live at both
        // 1280 and 1440, measured ink to ink off five of the owner's own live
        // shots — bg-{plp,search,pdp}-1280.png and bg-{search,plp}-1440.png —
        // through a colour-managed canvas. The WORDMARK is the control: 22.5
        // from band top on live and on ours, so the band origins agree and the
        // side zones genuinely do not. Full table, and why the two insets differ
        // from each other, in theme/tokens/brand-bg/header.css beside the arm.
        //
        // Gated at 1025 because that is where this composition flips; below it
        // the classes are not emitted at all, so BG's measured 390/768/1024
        // geometry cannot move. NM never reads either token — these strings are
        // BG's alone.
        leftZone: 'flex-1 min-w-0 flex items-center bgnav:items-start bgnav:pt-(--header-left-zone-top-inset)',
        rightZone:
            'flex-1 min-w-0 flex items-center justify-end bgnav:items-start bgnav:pt-(--header-right-zone-top-inset)',
        // UNMEASURED-BY-DESIGN: live BG's inter-link gap is `mr-[26px]`
        // (bg-home-d-1280/App.tsx:96, :99) with `pr-5` before Bag (:106). Those
        // are BG VALUES and belong in src/theme/tokens/brand-bg.css, which is
        // outside this change's file ownership — so the row reuses the shared
        // --header-icon-row-gap token and the 26/20px pair is FLAGGED, not
        // guessed at, in the change report.
        utilityRow: 'flex items-center gap-x-(--header-icon-row-gap)',
        // Below 1025 the offset is dropped — see `rightZoneOffset`'s docblock for
        // why the harvested 18px never painted on live. Measured on our app,
        // brand `bg`, clientWidth 390: bag svg box right edge 344 -> 373.5, i.e.
        // a right inset of 46 -> 16.5, against live's 16.5 read off
        // .reference/live-shots/bg-search-390.png.
        rightZoneOffset: 'bgnav:right-(--header-right-zone-inset)',
        // LIVE'S DECLARED BOX, APPLIED — and now box and ink AGREE, because the
        // art inside it is live's own. This value used to be a 22 x 25.5
        // arbitrary-value pair — a box fitted to live's scanned ink while the
        // DRAWING in it was NM's. (Written as prose rather than as the class
        // string it was: Tailwind's scanner treats any candidate in a scanned
        // source file as a usage, so spelling a retired utility here would
        // resurrect it in the bundle and hand the dead-token ratchet a rule
        // nothing renders.) That is the whole shape of the defect: the box was
        // right,
        // so every box-level comparison reported parity, and the mark on screen
        // still belonged to the other brand.
        //
        // Live BG declares `width="30" height="30" viewBox="0 0 30 30"` on the
        // `alt="Shopping Bag containing N items"` img, decoded from four
        // independent BG dumps — see components/icons/bg-bag-icon.tsx for the
        // transcription, the subpath table and the provenance. 30px is that
        // value verbatim, and at it live's art renders at scale 1.
        //
        // BOX vs INK, stated because they are not the same number here. The art
        // carries its own margins — insets 4.000 left, 4.400 right, 1.881 top,
        // 2.592 bottom inside the 30 box — so a 30px BOX paints 21.600 x 25.527
        // of INK, against live's colour-managed scan of 21.5 x 25.5 off
        // .reference/live-shots/bg-search-390.png at clientWidth 390. Two
        // independent instruments, 0.1px apart. The previous 22 x 25.5 box
        // painted 22.00 x 25.00 (measured by pixel scan, not by rect: the old
        // art overflowed its own viewBox and getBoundingClientRect reported the
        // unclipped 23.07).
        //
        // MUST CONTAIN A `size-` TOKEN — see the field docblock; the shadcn
        // Button squashes any svg without one to 16x16.
        cartGlyphBox: 'size-[30px]',
        // ZERO, because the 4.5px this used to carry was a COMPENSATION for art
        // that had no margins of its own, and live's art has them.
        //
        // Live's own control is a bare `inline-block relative` anchor with no
        // padding (bg-home-m-375/App.tsx:104), and live's bag ink sits 16.5px
        // from the viewport edge at 390. Live spends that as row padding plus
        // the asset's own right inset. Ours now does the same: the row pins this
        // control's right edge 12px from the edge (measured, clientWidth 390 —
        // control right 378), and the asset contributes its own 4.4, for 16.4
        // against live's 16.5.
        //
        // Keeping the 4.5 would have DOUBLE-COUNTED the inset. Measured, not
        // derived — the retired padding was put back as an inline style on the
        // shipped build and the same nodes re-read at clientWidth 390: the
        // control's right edge stays pinned at 378 either way, and the ink
        // inset goes 16.40 -> 20.90 against live's 16.5. The compensation and
        // the thing it compensated for retire together, which is the only way a
        // compensation should ever go.
        //
        // Below md, and — since the 1025 gate below was made disjoint — again
        // at 1025+, where it is the only thing setting `padding-inline` and so
        // supplies the LEFT zero live's Bag anchor has. Between md and 1025
        // `cartBoxPadX` overrides it with the symmetric token box, and there the
        // control has a fixed width so the box centres rather than pinning its
        // right edge (measured at 768: control 702..756, box centred, net ink
        // movement +0.4px).
        cartTriggerMobilePadX: 'px-0',
        // Desktop bag pads on the RIGHT ONLY — live's Bag anchor is `pl-0` and
        // all of its horizontal padding lives on the wrapper's `pr-5`
        // (bg-home-d-1280/App.tsx:106-107). The 768-1024 mobile tier, measured
        // separately, keeps the symmetric box it already had; at 1025 — where
        // the composition also flips — the left goes to zero and only the right
        // pad survives.
        //
        // THE TWO ARMS ARE DISJOINT MEDIA CONDITIONS, AND THAT IS THE WHOLE
        // POINT. This field previously read
        // `md:px-(--header-cart-box-pad-x) bgnav:pl-0`, which is the same
        // intent expressed as an OVERRIDE — and it never took effect. Measured
        // on the running app, brand `bg`, clientWidth 1280, guards asserting
        // href / clientWidth / data-brand inside the evaluate: the button's
        // computed `padding-left` read 16px with `.bgnav\:pl-0` present on the
        // element and its `@media (width >= 1025px)` matching, so the painted
        // Wishlist-to-Bag gap sat at 42.0 against Sign-in-to-Wishlist's 26.0.
        //
        // WHY THE OVERRIDE LOST, because it is not specificity and it is not
        // this element. `--breakpoint-bgnav` is declared in our own `@theme`
        // block (theme/tailwind.css:72), and Tailwind emits that variant's
        // whole group BEFORE the built-in breakpoint groups rather than in
        // pixel order. Read off the served stylesheet in the browser, by rule
        // index: bgnav 2873-2924, sm 2926-3064, md 3072-3953, lg 3552-3690,
        // xl 3692-3724. So `.bgnav\:pl-0` sits at 2922 and
        // `.md\:px-(--header-cart-box-pad-x)` at 3440; both media queries match
        // at 1280, both selectors are (0,1,0), and the later one wins.
        //
        // THE GENERAL FORM, since it is not confined to padding: at any width
        // at or above 1025, a `bgnav:` utility LOSES to an `sm:`/`md:`/`lg:`/
        // `xl:` utility that sets the same property on the same element. `bgnav:`
        // is only reliable against UNPREFIXED utilities, which do sort earlier.
        // Anywhere `bgnav:` must beat a built-in breakpoint, gate the built-in
        // arm with `max-bgnav:` so the two conditions cannot both match, exactly
        // as this field now does.
        //
        // Left padding at 1025+ therefore comes from `cartTriggerMobilePadX`'s
        // unprefixed `px-0`, which no longer has a competing `px-` above it.
        // Measured effect at 1280: the Wishlist-to-Bag painted gap closes
        // 42.0 -> 26.0, matching Sign-in-to-Wishlist exactly, while the Bag's
        // painted right inset stays at 102.0 — the row is right-aligned, so
        // only the box's LEFT edge moves.
        cartBoxPadX: 'md:max-bgnav:px-(--header-cart-box-pad-x) bgnav:pr-(--header-cart-box-pad-x)',
        // LIVE'S DECLARED BOX, APPLIED — and the ink aspect now matches too.
        //
        // BOX: live BG declares `w-[30px]` on the hamburger img at 375 and 440
        // (bg-home-m-375, bg-home-m-440 — each capture emits the page twice, so
        // that is one occurrence apiece). 30px is that value verbatim.
        //
        // INK: 26.34 x 14.84, aspect 1.774, measured in-browser at clientWidth
        // 390 against live BG's scanned 26.5 x 15.0. The box did not move to get
        // there — components/icons/bg-menu-icon.tsx replaced the DRAWING.
        //
        // THIS PARAGRAPH USED TO SAY THE ASSET COULD NOT BE RECOVERED, on the
        // grounds that the decompiler rehosts live's hamburger to a
        // storage.googleapis.com URL. That was true of the CAPTURES and false of
        // the tree: the raw dumps preserve live's own markup, and the asset is
        // inline base64 there in all four BG dumps. The record is corrected
        // rather than deleted, because the mistake is the reusable part — a
        // negative about "the evidence" that was really a negative about one
        // tier of it. The same error hid NM's hamburger, one arm up.
        mobileMenuGlyphBox: 'size-[30px]',
        // unmeasured — live BG declares a box on the hamburger IMG (the 30px the
        // field above carries, bg-home-m-375 / bg-home-m-440) and nothing on the
        // control around it, so no live rect exists for the button itself. Empty
        // keeps BG on the stock `size-9` it renders today, byte-identical, which
        // is what makes NM's row unable to move this brand. Retiring this entry
        // needs a capture that carries the CONTROL's box, not the image's.
        mobileMenuBox: '',
    },
};
